// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useOtChainState } from '@react/redux/store'
import { ChainConfiguration, StageConfiguration } from '@shared/DeploymentConfigTypes'
import { logOT } from '@shared/loggingShared'
import { LytReservesCTO, LytReservesCTOAllocation } from '@shared/LytReservesCTO'
import { displayName } from '@shared/PoolBaseCTO'
import { PoolDynamicOverviewCTO } from '@shared/PoolDynamicCTO'
import React, { useEffect, useState } from 'react'
import { ReserveActionPlanClass } from './ReserveActionPlanClass'

import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import { lytPoolReservePut } from '@react/lyt/LytFetchers'
import { ethers, ZeroAddress } from 'ethers'

// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.
export default function LytIssuerReserveTargetsEdit(props: {
  reserveActionPlanClass: ReserveActionPlanClass
  refreshCallback: () => void
}): React.ReactElement {
  const {
    stageConfiguration: { isLyt, serverApiBaseUrl: lytServerApiBaseUrl, restAPIKey: lytRestAPIKey },
    stageConfiguration: lytStageConfiguration,
    cognitoUser,
  } = useOtChainState()
  const { reserveActionPlanClass } = props

  const { lytPoolAddr } = reserveActionPlanClass?.reserveActionPlanStateDataCTO ?? {
    lytPoolAddr: ethers.ZeroAddress,
  }

  const [reserveAllocationInEdit, setReserveAllocationInEdit] = useState<LytReservesCTO[]>([])

  const otStageConfiguration = lytStageConfiguration?.parentStageConfiguration as StageConfiguration

  const otChainConfiguration = lytStageConfiguration?.parentChainConfiguration as ChainConfiguration

  useEffect(() => {
    if (reserveActionPlanClass?.reservesWithStats) {
      const reserveAllocationInEdit = reserveActionPlanClass.reservesWithStats.map((r) => r.lytReservesCTO)
      setReserveAllocationInEdit(reserveAllocationInEdit)
    }
  }, [reserveActionPlanClass?.reservesWithStats])

  const saveReserveAllocation = () => {
    if (!reserveAllocationInEdit || !lytServerApiBaseUrl || !lytRestAPIKey) {
      return
    }
    logOT.info('Save Reserve Allocation', reserveAllocationInEdit)

    logOT.info('Saving Reserve Allocation', JSON.stringify(reserveAllocationInEdit, null, 2))

    const lytReservesCTOAllocation: LytReservesCTOAllocation = {
      lytPoolAddr: lytPoolAddr,
      timestamp: Math.floor(Date.now() / 1000),
      reserves: reserveAllocationInEdit,
    }

    lytPoolReservePut(lytServerApiBaseUrl, lytRestAPIKey, lytReservesCTOAllocation, cognitoUser).then(
      (_reserveAllocation: LytReservesCTOAllocation) => {
        logOT.info('Reserve Allocation', _reserveAllocation)
        if (_reserveAllocation.reserves) {
          setReserveAllocationInEdit(_reserveAllocation.reserves)
        }
        props.refreshCallback()
      }
    )
  }

  const totalPercentReservesMax = reserveAllocationInEdit.reduce((acc, reserve) => acc + reserve.percentReservesMax, 0)
  const duplicatePriorities = reserveAllocationInEdit.some(
    (reserve) =>
      reserveAllocationInEdit.some(
        (r2: LytReservesCTO) => r2.id !== reserve.id && r2.depositPriority === reserve.depositPriority
      ) ||
      reserveAllocationInEdit.some(
        (r2: LytReservesCTO) => r2.id !== reserve.id && r2.withdrawalPriority === reserve.withdrawalPriority
      )
  )

  return (
    <Paper sx={{ padding: 3, border: '1px solid', borderColor: 'grey.200' }}>
      <Box data-testid="lyt-issuer-reserve-targets">
        {isLyt && (
          <Box key="header" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Typography variant="h3">Reserve Targets</Typography>
            {addReserveRowButton()}
            <Button
              disabled={duplicatePriorities || totalPercentReservesMax < 10000}
              variant="contained"
              size="small"
              onClick={() => {
                saveReserveAllocation()
              }}
            >
              Save
            </Button>
            {totalPercentReservesMax < 10000 && <Typography variant="caption">Sum of reserves must == 100%</Typography>}
          </Box>
        )}

        <Box
          sx={{
            overflowX: 'scroll',
            width: '100%',
            '&::-webkit-scrollbar': {
              height: '12px',
              display: 'block',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#a8a8a8',
              },
            },
            scrollbarWidth: 'auto',
            scrollbarColor: '#c1c1c1 #f1f1f1',
          }}
        >
          <Table key="table" sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ paddingX: 2 }}>
                  <Typography variant="caption">Pool Name</Typography>
                </TableCell>

                <TableCell sx={{ paddingX: 2, width: 80 }}>
                  <Typography variant="caption">Actual Reserves</Typography>
                </TableCell>
                <TableCell sx={{ paddingX: 2 }}>
                  <Typography variant="caption">APY</Typography>
                </TableCell>
                <TableCell sx={{ paddingX: 1, width: 140, textAlign: 'center' }}>
                  <Tooltip title="Minimum and maximum percentage of reserves to maintain in this pool">
                    <Typography variant="caption">Reserves Range (Min-Max %)</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ paddingX: 1, maxWidth: 100 }}>
                  <Tooltip title="Priority for deposits (1 = highest priority)">
                    <Typography variant="caption">Deposit Priority</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ paddingX: 1, maxWidth: 100 }}>
                  <Tooltip title="Priority for withdrawals (1 = highest priority)">
                    <Typography variant="caption">Withdraw Priority</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ paddingX: 2, textAlign: 'center' }}>
                  <Typography variant="caption">Action</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reserveActionPlanClass?.reservesWithStats.map((reserve) =>
                reserveRow({ reserveCTO: reserve.lytReservesCTO, reserveAllocationInEdit })
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Paper>
  )
  function addReserveRowButton(): React.ReactElement {
    if (!lytPoolAddr || !reserveAllocationInEdit) {
      return <></>
    }
    return (
      <FormControl sx={{ marginLeft: 2, marginRight: 2, minWidth: 80 }}>
        <InputLabel id="Reserve-pool-label">Add Pool</InputLabel>
        <Select
          labelId="Reserve-pool-label"
          value={ZeroAddress}
          label="Add Pool"
          placeholder="Select Pool"
          onChange={(e) => {
            const _poolAddr = e.target.value
            const _selectedPoolOverview =
              reserveActionPlanClass?.reserveActionPlanStateDataCTO.possibleReservePools.find(
                (pool) => pool.poolAddr === _poolAddr
              )
            if (_selectedPoolOverview) {
              const newReserve: LytReservesCTO = {
                isLiquidityAsset: false,
                id: ethers.uuidV4(ethers.randomBytes(16)),
                stageName: otStageConfiguration.stageName,
                chainConfigurationName: otChainConfiguration.chainConfigurationName,
                chainId: otChainConfiguration.chainId,
                poolAddr: _selectedPoolOverview.poolAddr,
                poolName: displayName(_selectedPoolOverview),
                assetBalance: '0',
                apy: _selectedPoolOverview.indicativeInterestRate,
                percentReservesMin: 0,
                percentReservesMax: 0,
                poolToken: _selectedPoolOverview.symbol,
                tokenBalance: '0',
                currentAssetValue: '0',
                depositPriority: 1,
                withdrawalPriority: 1,
              }

              setReserveAllocationInEdit([...reserveAllocationInEdit, newReserve])
            }
          }}
        >
          <MenuItem key={ZeroAddress} value={ZeroAddress}>
            <Typography variant="bodyBold">Select Pool to Add</Typography>
          </MenuItem>
          {reserveActionPlanClass?.reserveActionPlanStateDataCTO.possibleReservePools
            .filter((pool) => {
              const existingReserve = reserveAllocationInEdit.find((r) => r.poolAddr === pool.poolAddr)
              return (
                !existingReserve && pool.liquidityAssetAddr === reserveActionPlanClass?.nativeLiquidityAsset?.address
              )
            })
            .map((pool: PoolDynamicOverviewCTO) => (
              <MenuItem key={pool.poolAddr} value={pool.poolAddr}>
                {displayName(pool)}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    )
  }
}
function reserveRow(props: {
  reserveCTO: LytReservesCTO
  reserveAllocationInEdit: LytReservesCTO[]
}): React.ReactElement {
  const { reserveCTO } = props
  const [minPercentReserves, setMinPercentReserves] = useState((reserveCTO.percentReservesMin / 100).toString())
  const [maxPercentReserves, setMaxPercentReserves] = useState((reserveCTO.percentReservesMax / 100).toString())
  const [depositPriority, setDepositPriority] = useState(reserveCTO.depositPriority.toString())
  const [withdrawalPriority, setWithdrawalPriority] = useState(reserveCTO.withdrawalPriority.toString())

  return (
    <TableRow key={reserveCTO.id}>
      <TableCell sx={{ paddingX: 2 }}>
        <Typography variant="caption">{reserveCTO.poolName}</Typography>
      </TableCell>

      <TableCell sx={{ paddingX: 2 }}>
        <Typography variant="caption">{(parseFloat(reserveCTO.apy) / 100).toFixed(2)}%</Typography>
      </TableCell>
      <TableCell sx={{ paddingX: 1, width: 140 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', maxWidth: 130 }}>
          <Tooltip title="Minimum percentage of reserves to maintain in this pool">
            <TextField
              size="small"
              label="Min %"
              value={minPercentReserves}
              onChange={(e) => {
                const value = e.target.value
                setMinPercentReserves(value)
                if (value === '' || (/^\d*\.?\d*$/.test(value) && Number(value) >= 0 && Number(value) <= 100)) {
                  reserveCTO.percentReservesMin = Math.round(Number(value) * 100)
                }
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              error={reserveCTO.percentReservesMin > reserveCTO.percentReservesMax}
              sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
            />
          </Tooltip>
          <Tooltip title="Maximum percentage of reserves to maintain in this pool">
            <TextField
              size="small"
              label="Max %"
              value={maxPercentReserves}
              onChange={(e) => {
                const value = e.target.value
                setMaxPercentReserves(value)
                if (value === '' || (/^\d*\.?\d*$/.test(value) && Number(value) >= 0 && Number(value) <= 100)) {
                  reserveCTO.percentReservesMax = Math.round(Number(value) * 100)
                }
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              error={reserveCTO.percentReservesMin > reserveCTO.percentReservesMax}
              sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
            />
          </Tooltip>
          {reserveCTO.percentReservesMin > reserveCTO.percentReservesMax && (
            <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}>
              Min cannot exceed Max
            </Typography>
          )}
        </Box>
      </TableCell>

      <TableCell sx={{ paddingX: 1 }}>
        <Tooltip title={`Priority for deposits (1-N). Lower numbers get deposits first.`}>
          <TextField
            size="small"
            label="Priority"
            value={depositPriority}
            onChange={(e) => {
              const value = e.target.value
              const numValue = Number(value)
              setDepositPriority(value)
              if (value === '' || (/^\d+$/.test(value) && numValue >= 1)) {
                reserveCTO.depositPriority = Number(value)
              }
            }}
            helperText={`1-N`}
            sx={{ minWidth: 100 }}
            error={props.reserveAllocationInEdit.some(
              (r2: LytReservesCTO) => r2.id !== reserveCTO.id && r2.depositPriority === reserveCTO.depositPriority
            )}
          />
        </Tooltip>
      </TableCell>

      <TableCell sx={{ paddingX: 1 }}>
        <Tooltip title={`Priority for withdrawals (1-N). Lower numbers get withdrawn from first.`}>
          <TextField
            size="small"
            label="Priority"
            value={withdrawalPriority}
            onChange={(e) => {
              const value = e.target.value
              const numValue = Number(value)
              setWithdrawalPriority(value)
              if (value === '' || (/^\d+$/.test(value) && numValue >= 1)) {
                reserveCTO.withdrawalPriority = Number(value)
              }
            }}
            helperText={`1-N`}
            sx={{ minWidth: 100 }}
            error={props.reserveAllocationInEdit.some(
              (r2: LytReservesCTO) => r2.id !== reserveCTO.id && r2.withdrawalPriority === reserveCTO.withdrawalPriority
            )}
          />
        </Tooltip>
      </TableCell>
    </TableRow>
  )
}
